import * as React from 'react';
import styled from 'styled-components';
import { ResponsivePie } from '@nivo/pie';
import { Tooltip as InfoHint } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import { FranchiseType } from '../../api/franchise';
import { SelectInputWithSelectAll } from '../../atoms';
import { BRANCH_INCOME_CHART_DESCRIPTION } from '../../constants/graphDescriptions';

const SALES_COLOR = '#0b6b9b'; // var(--primary-600)
const PROJECTED_COLOR = '#cfe6f3'; // var(--primary-100)

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const percentFormat = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const FranchiseIncomeChartWidget = ({
  data,
  franchises,
  onSelectChange,
}: Props) => {
  const [
    selectedIds,
    setSelectedIds,
  ] = React.useState<number[] | null>(null);

  const selectedFranchises = React.useMemo(() => {
    if (!selectedIds || !franchises) return [];
    const idSet = new Set(selectedIds);
    return franchises.filter(f => idSet.has(f.id));
  }, [selectedIds, franchises]);

  const allSelected =
    selectedFranchises.length > 0 &&
    franchises &&
    selectedFranchises.length === franchises.length;

  const sales = Number(data?.sales) || 0;
  const projected = Number(data?.projectedSales) || 0;
  const achievedRatio =
    projected > 0 ? sales / projected : 0;
  const achievedLabel = data
    ? percentFormat.format(
        Number.isFinite(achievedRatio) ? achievedRatio : 0,
      )
    : '0%';

  const hasData = !!data && (sales !== 0 || projected !== 0);

  // Feed the doughnut exactly two numeric slices — never the raw API payload,
  // which may carry extra/non-numeric keys that would render as `$NaN` slices.
  const pieData = [
    { id: 'Sales', label: 'Sales', value: sales },
    { id: 'Projected Sales', label: 'Projected Sales', value: projected },
  ];

  const sliceColor = (slice: any) =>
    slice.id === 'Sales' ? SALES_COLOR : PROJECTED_COLOR;

  return (
    <Container>
      <Header>
        Branch Income Chart
        <InfoHint
          arrow
          placement="top"
          title={BRANCH_INCOME_CHART_DESCRIPTION}
        >
          <InfoIcon fontSize="small" />
        </InfoHint>
      </Header>
      {franchises && franchises.length > 0 && (
        <SelectContainer>
          <SelectInputWithSelectAll
            data={franchises}
            valueKey="id"
            labelKey="name"
            onMenuClose={(selected, isCanceled) => {
              if (!isCanceled) {
                setSelectedIds(
                  selected?.map((item: any) => item.id) ||
                    null,
                );
              }
              if (onSelectChange) {
                onSelectChange(selected, isCanceled);
              }
            }}
            placeholder="Select branches from the list..."
          />
        </SelectContainer>
      )}

      <SelectionBar>
        <SelectionLabel>Showing</SelectionLabel>
        {allSelected ||
        !selectedFranchises ||
        selectedFranchises.length === 0 ? (
          <AllChip>All branches</AllChip>
        ) : (
          <ChipRow>
            {selectedFranchises.slice(0, 4).map(f => (
              <Chip key={f.id} title={f.name}>
                {f.name}
              </Chip>
            ))}
            {selectedFranchises.length > 4 && (
              <ChipMore
                title={selectedFranchises
                  .slice(4)
                  .map(f => f.name)
                  .join(', ')}
              >
                +{selectedFranchises.length - 4} more
              </ChipMore>
            )}
          </ChipRow>
        )}
      </SelectionBar>

      {hasData ? (
        <Content>
          <Pie>
            <ResponsivePie
              data={pieData}
              margin={{ top: 28, right: 96, bottom: 28, left: 96 }}
              innerRadius={0.68}
              padAngle={1.2}
              cornerRadius={4}
              colors={sliceColor}
              borderWidth={0}
              enableRadialLabels
              radialLabel={(d: any) =>
                currencyFormat.format(Number(d.value) || 0)
              }
              radialLabelsSkipAngle={2}
              radialLabelsTextColor="#3b424e"
              radialLabelsLinkColor={{ from: 'color' }}
              radialLabelsLinkStrokeWidth={1.5}
              enableSliceLabels={false}
              isInteractive={false}
              theme={{
                fontFamily:
                  "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif",
                labels: {
                  text: {
                    fontSize: 12,
                    fontWeight: 600,
                  },
                },
              }}
            />
            <PieCenter>
              <PieCenterValue>
                {achievedLabel}
              </PieCenterValue>
              <PieCenterLabel>Achieved</PieCenterLabel>
            </PieCenter>
          </Pie>
          <Legend>
            <LegendRow>
              <LegendDot color={SALES_COLOR} />
              <LegendName>Sales</LegendName>
              <LegendValue>
                {currencyFormat.format(sales)}
              </LegendValue>
            </LegendRow>
            <LegendRow>
              <LegendDot color={PROJECTED_COLOR} />
              <LegendName>Projected Sales</LegendName>
              <LegendValue>
                {currencyFormat.format(projected)}
              </LegendValue>
            </LegendRow>
            <LegendDivider />
            <LegendRow>
              <LegendDot color="transparent" />
              <LegendName>Achieved Percentage</LegendName>
              <LegendValueAccent>
                {achievedLabel}
              </LegendValueAccent>
            </LegendRow>
          </Legend>
        </Content>
      ) : (
        <EmptyState>
          <EmptyTitle>No income to display</EmptyTitle>
          <EmptySubtitle>
            Select different branches or check back once
            invoices land in this period.
          </EmptySubtitle>
        </EmptyState>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  margin: 0;
`;

const InfoIcon = styled(InfoOutlinedIcon)`
  color: var(--neutral-400);
  cursor: help;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary-600);
  }
`;

const SelectContainer = styled.div`
  margin: 16px 0 8px;
`;

const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 4px 0 16px;
  padding: 10px 12px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 8px;
`;

const SelectionLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  flex-shrink: 0;
`;

const ChipRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100);
  font-size: 0.75rem;
  font-weight: 600;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipMore = styled(Chip)`
  background: var(--neutral-100);
  color: var(--neutral-700);
  border-color: var(--neutral-200);
  cursor: help;
`;

const AllChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-size: 0.75rem;
  font-weight: 600;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Pie = styled.div`
  position: relative;
  width: 100%;
  height: 320px;
`;

const PieCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const PieCenterValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.1;
`;

const PieCenterLabel = styled.div`
  margin-top: 4px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const Legend = styled.div`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
`;

const LegendDot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ color }) => color};
  flex-shrink: 0;
`;

const LegendName = styled.span`
  flex: 1;
  color: var(--neutral-700);
`;

const LegendValue = styled.span`
  font-weight: 600;
  color: var(--neutral-900);
`;

const LegendValueAccent = styled(LegendValue)`
  color: var(--primary-700);
`;

const LegendDivider = styled.div`
  height: 1px;
  background: var(--border);
  margin: 4px 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  padding: 64px 24px;
  border: 1px dashed var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  margin-top: 8px;
`;

const EmptyTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--neutral-700);
`;

const EmptySubtitle = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
  max-width: 320px;
  line-height: 1.5;
`;

interface Props {
  data: any;
  franchises: FranchiseType[];
  onSelectChange?: (
    selected: any[] | null,
    isCanceled: boolean,
  ) => void;
}

export default FranchiseIncomeChartWidget;
